export const useVideoPage = async () => {
  const nuxtApp = useNuxtApp()
  const categoryEventName = 'Video'
  const eventName = 'click_video_category'

  const {
    asyncCategory,
    breadcrumbs,
    items: videos,
    page,
    perPage,
    total,
  } = await useListApi<VideoMeta>({
    categoryApiPath: '/video-categories',
    itemPath: '/videos?populate=videoCategory&orderBy=position&sortBy=asc',
    categoryField: 'videoCategoryId',
    categoryQueryMode: 'direct',
    componentName: 'Video',
    hasCategory: true,
    defaultPerPage: 24,
  })

  const { slugPageInfo, domain, getPath } = useNavigationMenu()
  const { isMainLanguage } = useLanguage()
  const currentCategory = computed(() => asyncCategory.value?.category)
  useCustomSeo({
    title: () => currentCategory.value?.metaTitleFront || slugPageInfo.value?.metaTitleFront,
    description: () =>
      currentCategory.value?.metaDescriptionFront ||
      slugPageInfo.value?.metaDescriptionFront ||
      slugPageInfo.value?.description,
    image: () => currentCategory.value?.ogImage?.large || slugPageInfo.value?.ogImage?.large,
    noindex: () => !isMainLanguage.value && videos.value.length === 0,
  })

  const { analyticEvent, fbPixelEvent } = useAnalytic()
  const eventVideo = (video: string) => {
    analyticEvent({
      ga4ActionName: 'play_video',
      ga4Label: video,
    })
    fbPixelEvent({
      fbCategory: 'trackCustom',
      fbEvent: 'Video - Play',
      fbValue: { label: video },
    })
  }

  // Helper function to extract YouTube video ID
  function getYoutubeId (url: string): string {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    return (match && match[2].length === 11) ? match[2] : ''
  }

  // Add video collection schema
  const { currentLocaleCode } = useLanguage()
  nuxtApp.runWithContext(() => useSchemaOrg([
    defineWebPage({
      '@type': 'CollectionPage',
      '@id': `${domain.value}${getPath({ componentName: 'Video' })}#webpage`,
      name: currentCategory.value?.metaTitleFront || slugPageInfo.value?.metaTitleFront,
      url: `${domain.value}${getPath({ componentName: 'Video' })}`,
      inLanguage: currentLocaleCode.value,
      isPartOf: { '@id': `${domain.value}/#website` },
      publisher: { '@id': `${domain.value}/#organization` },
      mainEntity: videos.value.map(video => ({
        '@type': 'VideoObject',
        name: video.title || '',
        description: video.description || '',
        embedUrl: video.youtubeUrl || '',
        thumbnailUrl: video.youtubeUrl ? `https://img.youtube.com/vi/${getYoutubeId(video.youtubeUrl)}/hqdefault.jpg` : '',
        uploadDate: video.createdAt || '',
        creator: { '@id': `${domain.value}/#organization` },
      })),
    }),
    defineItemList({
      '@id': `${domain.value}${getPath({ componentName: 'Video' })}#category-list`,
      itemListElement: asyncCategory.value?.categoryButtonItems?.map((cat, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: cat.title,
        url: `${domain.value}${cat.path}`,
      })),
    }),
  ]))

  return {
    categoryEventName,
    eventName,
    asyncCategory,
    breadcrumbs,
    videos,
    page,
    perPage,
    total,
    eventVideo,
  }
}
